import { useState } from 'react';
import QRCode from 'qrcode';
import { ShieldCheck } from '../../icons';
import {
  adminLogin, adminMfaVerify, adminMfaEnrollStart, adminMfaEnrollConfirm, mfaMethodOf,
} from '../../api/auth';
import type { AdminUser } from '../../store/adminAuthStore';
import { useAdminAuthStore } from '../../store/adminAuthStore';
import './LoginPage.css';

/**
 * admin 독립 로그인 — 이메일/비번 → (ADMIN MFA 강제 시) MFA 코드 → 콘솔.
 * 비-admin 은 BFF 가 403(ADMIN_ONLY) → 안내. lucide only.
 */
export function LoginPage() {
  const setAuthenticated = useAdminAuthStore((s) => s.setAuthenticated);
  const [step, setStep] = useState<'credentials' | 'mfa' | 'enroll' | 'backup'>('credentials');
  // 2026-10-15 콘솔 2차 인증 «등록» — 콘솔 계정이 콘솔 자신의 표로 옮겨 와 콘솔 안에서 등록한다.
  const [enroll, setEnroll] = useState<{ secret: string; qr: string } | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [pendingUser, setPendingUser] = useState<AdminUser | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function errMessage(e: unknown): string {
    const ax = e as { response?: { status?: number; data?: { error?: string; message?: string } } };
    const d = ax.response?.data;
    if (ax.response?.status === 403 || d?.error === 'ADMIN_ONLY') return '관리자 권한이 필요합니다.';
    if (ax.response?.status === 401) return '이메일 또는 비밀번호가 올바르지 않습니다.';
    return d?.message ?? '로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.';
  }

  async function handleCredentials(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const r = await adminLogin(email.trim(), password);
      if (r.mfa_required) {
        if (r.mfa_enroll_required) {
          // 콘솔 자체 인증이면 여기서 등록한다. 종전 서버(404)면 종전 안내 그대로.
          try {
            const st = await adminMfaEnrollStart(r.temp_token ?? '');
            const qr = await QRCode.toDataURL(st.otpauth_uri, { margin: 1, width: 184 });
            setTempToken(r.temp_token ?? '');
            setEnroll({ secret: st.secret, qr });
            setCode('');
            setStep('enroll');
          } catch (en) {
            const status = (en as { response?: { status?: number } }).response?.status;
            setError(status === 404
              ? 'MFA 등록이 필요합니다. 메인 앱에서 MFA 를 먼저 등록해 주세요.'
              : errMessage(en));
          }
          return;
        }
        setTempToken(r.temp_token ?? '');
        setStep('mfa');
      } else if (r.user) {
        setAuthenticated(r.user);
      }
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleMfa(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const r = await adminMfaVerify(tempToken, code.trim(), mfaMethodOf(code.trim()));
      if (r.user) setAuthenticated(r.user);
      else setError('인증 코드가 올바르지 않습니다.');
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleEnrollConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const r = await adminMfaEnrollConfirm(tempToken, code.trim());
      if (!r.user) { setError('인증 코드가 올바르지 않습니다.'); return; }
      // 백업 코드는 이번 한 번만 보인다 — 받아 적을 때까지 콘솔로 넘기지 않는다.
      setPendingUser(r.user);
      setBackupCodes(r.backup_codes ?? []);
      setStep('backup');
    } catch (e) {
      const ax = e as { response?: { status?: number; data?: { message?: string } } };
      setError(ax.response?.status === 401 ? '인증 코드가 올바르지 않습니다.' : errMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (step === 'enroll' && enroll) {
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <div className="admin-login-brand">
            <ShieldCheck size={28} />
            <h1>2단계 인증 등록</h1>
          </div>
          <form onSubmit={handleEnrollConfirm} className="admin-login-form">
            <p className="admin-login-hint">
              인증 앱(Google Authenticator 등)으로 QR 코드를 찍거나 아래 키를 입력한 뒤, 앱에 나온 6자리 번호를 넣으세요.
            </p>
            <img className="admin-login-qr" src={enroll.qr} alt="2단계 인증 등록 QR 코드" width={184} height={184} />
            <code className="admin-login-secret" aria-label="수동 입력 키">{enroll.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
            <label htmlFor="enroll-code">인증 번호</label>
            <input id="enroll-code" type="text" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus disabled={busy} placeholder="6자리" />
            {error && <div className="admin-login-error" role="alert">{error}</div>}
            <button type="submit" disabled={busy}>{busy ? '확인 중…' : '등록하고 로그인'}</button>
            <button type="button" className="admin-login-back" onClick={() => { setStep('credentials'); setEnroll(null); setCode(''); setError(null); }} disabled={busy}>← 처음으로</button>
          </form>
        </div>
      </div>
    );
  }

  if (step === 'backup') {
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <div className="admin-login-brand">
            <ShieldCheck size={28} />
            <h1>백업 코드</h1>
          </div>
          <div className="admin-login-form">
            <p className="admin-login-hint">
              휴대폰을 잃었을 때 인증 번호 대신 쓰는 코드입니다. 지금 한 번만 보이니 안전한 곳에 적어 두세요. 각 코드는 한 번씩만 쓸 수 있습니다.
            </p>
            <ul className="admin-login-backup" aria-label="백업 코드">
              {backupCodes.map((c) => <li key={c}><code>{c}</code></li>)}
            </ul>
            <button type="submit" onClick={() => { if (pendingUser) setAuthenticated(pendingUser); }}>적어 두었습니다 — 콘솔로</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-login">
      <div className="admin-login-card">
        <div className="admin-login-brand">
          <ShieldCheck size={28} />
          <h1>Axi 관리 콘솔</h1>
        </div>
        {step === 'credentials' ? (
          <form onSubmit={handleCredentials} className="admin-login-form">
            <label htmlFor="email">이메일</label>
            <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus disabled={busy} />
            <label htmlFor="password">비밀번호</label>
            <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={busy} />
            {error && <div className="admin-login-error" role="alert">{error}</div>}
            <button type="submit" disabled={busy}>{busy ? '확인 중…' : '로그인'}</button>
          </form>
        ) : (
          <form onSubmit={handleMfa} className="admin-login-form">
            <p className="admin-login-hint">관리자 2단계 인증 코드를 입력하세요.</p>
            <label htmlFor="code">인증 코드</label>
            <input id="code" type="text" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus disabled={busy} placeholder="6자리 또는 백업 코드" />
            {error && <div className="admin-login-error" role="alert">{error}</div>}
            <button type="submit" disabled={busy}>{busy ? '검증 중…' : '인증'}</button>
            <button type="button" className="admin-login-back" onClick={() => { setStep('credentials'); setCode(''); setError(null); }} disabled={busy}>← 처음으로</button>
          </form>
        )}
      </div>
    </div>
  );
}
